import React, { useEffect, useState } from "react";
import { Boxes, Building2, Calculator, FileText, GitMerge, ImageIcon, Layers3, Network } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Carousel, CarouselContent, CarouselItem } from "../ui/carousel";
import { getApiBase } from "../../lib/apiBase";
import { useCarouselAutoplay } from "../../hooks/use-carousel-autoplay";

const FLOATING_BADGES = [
  { label: "Modèle BIM", icon: Layers3, position: "absolute -top-3 right-8 sm:right-16", testId: "bim3d-hero-badge-model" },
  { label: "Synthèse", icon: GitMerge, position: "absolute -bottom-3 left-10 sm:left-20", testId: "bim3d-hero-badge-synthesis" },
  { label: "Quantités", icon: Calculator, position: "absolute -bottom-3 right-10 sm:right-24", testId: "bim3d-hero-badge-quantities" },
];

const FloatingBadge = ({ badge }) => {
  const Icon = badge.icon;
  return (
    <div
      aria-hidden="true"
      className={`${badge.position} z-20 inline-flex items-center gap-2.5 rounded-full border border-[#cfe9e2] bg-white/92 px-5 py-2.5 text-[15px] font-semibold text-[#0f766e] shadow-[0_18px_36px_-16px_rgba(15,118,110,0.4)] backdrop-blur-md`}
      data-testid={badge.testId}
    >
      <Icon className="h-5 w-5 text-[#0f766e]" strokeWidth={2.2} />
      {badge.label}
    </div>
  );
};

const slides = [
  { title: "Slide projet BIM 3D", icon: ImageIcon, testId: "bim3d-hero-slide-project" },
  { title: "Slide coordination", icon: Network, testId: "bim3d-hero-slide-coordination" },
  { title: "Slide synthèse Navisworks", icon: Layers3, testId: "bim3d-hero-slide-navisworks" },
  { title: "Slide maquette tout corps d’état", icon: Boxes, testId: "bim3d-hero-slide-tce" },
];

const features = [
  {
    title: "Production BIM 3D",
    description: "Maquettes architecture, structure, MEP, façades et lots techniques.",
    icon: Boxes,
    testId: "bim3d-feature-card-production",
  },
  {
    title: "Synthèse de projet",
    description: "Coordination, détection de conflits, suivi des remarques et résolution technique.",
    icon: GitMerge,
    testId: "bim3d-feature-card-synthesis",
  },
  {
    title: "Livrables issus de la maquette",
    description: "Plans, coupes, nomenclatures, quantités, exports IFC/Navisworks.",
    icon: FileText,
    testId: "bim3d-feature-card-deliverables",
  },
  {
    title: "DOE & exploitation",
    description: "Maquettes structurées pour la maintenance, l’exploitation et le patrimoine immobilier.",
    icon: Building2,
    testId: "bim3d-feature-card-doe",
  },
];

const PlaceholderSlide = ({ item }) => {
  const Icon = item.icon;

  if (item.url) {
    return (
      <div className="relative" data-testid={item.testId}>
        {/* Ambient halo */}
        <div aria-hidden="true" className="pointer-events-none absolute -inset-x-6 -inset-y-8 -z-10 bg-[radial-gradient(60%_55%_at_50%_50%,_rgba(45,212,191,0.18),_transparent_70%),radial-gradient(40%_40%_at_15%_30%,_rgba(190,242,100,0.16),_transparent_70%),radial-gradient(40%_40%_at_85%_70%,_rgba(94,234,212,0.14),_transparent_70%)] blur-2xl" />
        <div className="relative min-h-[22rem] overflow-hidden rounded-[2.2rem] border border-[#cfe9e2] bg-[linear-gradient(180deg,_rgba(230,243,241,0.92)_0%,_rgba(248,252,243,0.96)_100%)] p-3 shadow-[0_28px_90px_-52px_rgba(15,118,110,0.4)] ring-1 ring-inset ring-white/70 sm:p-4 lg:min-h-[30rem] lg:p-5">
          {/* Subtle dot-grid texture */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-[2.2rem] opacity-[0.16] [background-image:radial-gradient(rgba(15,118,110,0.45)_1px,transparent_1px)] [background-size:22px_22px]" />
          {/* Floating brand badge */}
          <div aria-hidden="true" className="absolute left-6 top-6 z-10 inline-flex items-center gap-1.5 rounded-full border border-white/80 bg-white/75 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#0f766e] shadow-[0_8px_20px_-12px_rgba(15,118,110,0.35)] backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-[#0f766e]" />
            Aperçu projet
          </div>
          <div className="relative h-full min-h-[20rem] overflow-hidden rounded-[1.6rem] ring-1 ring-[#cfe9e2]/80 lg:min-h-[28rem]">
            <img alt={item.title || "Média BIM 3D"} className="h-full w-full object-cover" src={`${getApiBase()}${item.url}`} />
            {/* Green / cyan tint overlay */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 mix-blend-multiply bg-[linear-gradient(135deg,_rgba(16,185,129,0.10)_0%,_rgba(94,234,212,0.06)_50%,_rgba(45,212,191,0.10)_100%)]" />
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_50%,_transparent_55%,_rgba(15,118,110,0.10)_100%)]" />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/68 to-transparent px-8 pb-7 pt-14 text-white">
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-white/70">Média actif</div>
              <div className="mt-2 text-xl font-semibold">{item.title || "Média BIM 3D"}</div>
              {item.caption ? <div className="mt-2 text-sm text-white/80">{item.caption}</div> : null}
            </div>
          </div>
        </div>
        {/* Floating BIM badges */}
        {FLOATING_BADGES.map((badge) => (
          <FloatingBadge badge={badge} key={badge.label} />
        ))}
      </div>
    );
  }

  return (
    <div className="relative" data-testid={item.testId}>
      <div aria-hidden="true" className="pointer-events-none absolute -inset-x-6 -inset-y-8 -z-10 bg-[radial-gradient(60%_55%_at_50%_50%,_rgba(45,212,191,0.16),_transparent_70%)] blur-2xl" />
      <div className="relative min-h-[22rem] overflow-hidden rounded-[2.2rem] border border-[#cfe9e2] bg-[linear-gradient(180deg,_rgba(230,243,241,0.88)_0%,_rgba(248,252,243,0.96)_100%)] p-8 shadow-[0_28px_90px_-52px_rgba(15,118,110,0.32)] ring-1 ring-inset ring-white/70 lg:min-h-[30rem]">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-[2.2rem] opacity-[0.14] [background-image:radial-gradient(rgba(15,118,110,0.45)_1px,transparent_1px)] [background-size:22px_22px]" />
        <div className="relative flex h-full flex-col items-center justify-center gap-4 text-center text-[#0f766e]">
          <div className="rounded-2xl border border-white/70 bg-white/70 p-4 shadow-[0_14px_30px_-24px_rgba(15,118,110,0.28)]">
            <Icon className="h-8 w-8" />
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#0f766e]/70">Placeholder</div>
            <div className="mt-2 text-lg font-semibold text-slate-900">{item.title}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Bim3DHeroSection = () => {
  const [carouselApi, setCarouselApi] = useState();
  const [activeIndex, setActiveIndex] = useState(0);
  const [mediaSlides, setMediaSlides] = useState([]);

  useEffect(() => {
    if (!carouselApi) return undefined;

    const onSelect = () => setActiveIndex(carouselApi.selectedScrollSnap());
    onSelect();
    carouselApi.on("select", onSelect);

    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]);

  useEffect(() => {
    let active = true;
    const API = getApiBase();

    fetch(`${API}/api/bim-3d/media`)
      .then((response) => (response.ok ? response.json() : []))
      .then((data) => {
        if (!active) return;
        setMediaSlides(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (active) setMediaSlides([]);
      });

    return () => {
      active = false;
    };
  }, []);

  const slidesToRender = mediaSlides.length
    ? mediaSlides.map((item) => ({
        id: item.id,
        title: item.title || "Média BIM 3D",
        caption: item.caption || "",
        url: item.url,
        testId: `bim3d-hero-media-${item.id}`,
      }))
    : slides;
  const { handleMouseEnter, handleMouseLeave, handleManualNavigation } = useCarouselAutoplay(carouselApi, slidesToRender.length);

  return (
    <section className="relative overflow-hidden px-4 pb-16 pt-12 sm:px-6 lg:px-8 lg:pb-20 lg:pt-20" data-testid="bim3d-hero-section">
      <div className="absolute inset-x-0 top-0 h-[28rem] bg-[radial-gradient(circle_at_top,_rgba(45,212,191,0.12),_transparent_44%)]" />
      <div className="absolute left-[8%] top-[22%] h-48 w-48 rounded-full bg-[#d9f99d]/24 blur-3xl" />
      <div className="absolute right-[8%] top-[26%] h-56 w-56 rounded-full bg-[#99f6e4]/18 blur-3xl" />

      <div className="mx-auto max-w-[1560px]">
        <div className="mx-auto max-w-4xl text-center">
          <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]" data-testid="bim3d-hero-eyebrow">
            Offre BIM 3D + Synthèse
          </div>
          <h1 className="bim-display mt-5 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl lg:text-6xl" data-testid="bim3d-hero-title">
            BIM 3D + Synthèse
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-xl leading-9 text-slate-700" data-testid="bim3d-hero-subtitle">
            Des maquettes numériques coordonnées pour concevoir, construire et exploiter vos projets.
          </p>
          <p className="mx-auto mt-5 max-w-4xl text-base leading-8 text-slate-600 sm:text-lg" data-testid="bim3d-hero-description">
            Bimcubes produit des maquettes BIM tout corps d’état et accompagne la synthèse de projet : architecture, structure, MEP, façades, coordination, détection de conflits et livrables issus de la maquette.
          </p>
        </div>

        <div className="mt-14" data-testid="bim3d-hero-carousel-wrapper" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <Carousel className="mx-auto max-w-[1560px]" opts={{ loop: slidesToRender.length > 1 }} setApi={setCarouselApi}>
            <CarouselContent className="-ml-0">
              {slidesToRender.map((item) => (
                <CarouselItem className="pl-0" key={item.title}>
                  <PlaceholderSlide item={item} />
                </CarouselItem>
              ))}
            </CarouselContent>

            {slidesToRender.length > 1 ? (
              <div className="mt-6 flex items-center justify-between gap-4" data-testid="bim3d-hero-carousel-navigation">
                <div className="flex items-center gap-2" data-testid="bim3d-hero-carousel-dots">
                  {slidesToRender.map((slide, index) => (
                    <button
                      className={`h-2.5 rounded-full transition-all ${activeIndex === index ? "w-10 bg-[#0f766e]" : "w-2.5 bg-[#0f766e]/25 hover:bg-[#0f766e]/45"}`}
                      data-testid={`bim3d-hero-carousel-dot-${index + 1}`}
                      key={slide.title}
                      onClick={() => handleManualNavigation(() => carouselApi?.scrollTo(index))}
                      type="button"
                    />
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <Button className="rounded-full border-[#d9e7d5] bg-white/80 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid="bim3d-hero-carousel-prev-button" onClick={() => handleManualNavigation(() => carouselApi?.scrollPrev())} type="button" variant="outline">
                    Précédent
                  </Button>
                  <Button className="rounded-full border-[#d9e7d5] bg-white/80 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid="bim3d-hero-carousel-next-button" onClick={() => handleManualNavigation(() => carouselApi?.scrollNext())} type="button" variant="outline">
                    Suivant
                  </Button>
                </div>
              </div>
            ) : null}
          </Carousel>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-4" data-testid="bim3d-hero-feature-grid">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <Card
                className="group h-full rounded-[2rem] border border-[#d9e7d5]/90 bg-[linear-gradient(180deg,_rgba(255,255,255,0.92)_0%,_rgba(240,253,244,0.95)_100%)] shadow-[0_28px_76px_-50px_rgba(15,118,110,0.35)] backdrop-blur-xl transition duration-300 hover:-translate-y-1.5 hover:shadow-[0_36px_96px_-44px_rgba(15,118,110,0.4)]"
                data-testid={feature.testId}
                key={feature.title}
              >
                <CardContent className="p-8">
                  <div className="inline-flex rounded-2xl border border-white/70 bg-white/75 p-3 text-[#0f766e] shadow-[0_14px_30px_-24px_rgba(15,118,110,0.24)]">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="bim-display mt-5 text-[1.35rem] font-semibold text-slate-950" data-testid={`${feature.testId}-title`}>
                    {feature.title}
                  </h3>
                  <p className="mt-4 text-base leading-8 text-slate-600" data-testid={`${feature.testId}-description`}>
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};